<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import AppCard from '@/components/ui/AppCard.vue'
import DateField from '@/components/ui/DateField.vue'
import RankList, { type RankRow } from '@/components/ui/RankList.vue'
import SimplePaginator from '@/components/ui/SimplePaginator.vue'
import StatCard from '@/components/ui/StatCard.vue'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import { useAuthStore } from '@/stores/auth'
import {
  dashboardApi,
  DASHBOARD_EXPORTS,
  type DashboardSummary,
  type ProductRank,
  type SegmentPage,
} from '@/services/dashboard'
import { formatCurrency, formatShortDate } from '@/utils/format'
import { statusLabel, statusSeverity } from '@/utils/status'

/**
 * Panel de ventas.
 *
 * Las cifras llegan calculadas por el servidor y aqui solo se formatean. La
 * autorizacion real la aplica el backend con `dashboard.access`; ocultar la ruta
 * sin el permiso evita un 403 frustrante, pero no lo sustituye.
 *
 * Carga, error y "el rango no tiene documentos" son tres estados distintos: un
 * rango vacio con ceros sin explicacion parece un fallo de calculo, y un fallo de
 * red pintado como ceros hace que el usuario repita acciones que ya funcionaban.
 */

const authStore = useAuthStore()

const allowed = computed(() => authStore.hasPermission('dashboard.access'))

const summary = ref<DashboardSummary | null>(null)
const loading = ref(false)
const errorMessage = ref<string | null>(null)

const dateFrom = ref('')
const dateTo = ref('')

/**
 * Las cinco cifras de cabecera.
 *
 * Cada una lleva su contexto porque un número suelto no se puede interpretar:
 * «$ 10.746,37» no dice de qué mes es ni si son ventas o lo cotizado. El
 * `context` es lo que convierte la cifra en información, y por eso es
 * obligatorio en la práctica aunque el tipo lo permita omitir.
 *
 * El color marca de qué va cada tarjeta y no decora: si las cinco fueran del
 * color de marca, el color no distinguiría nada.
 *
 * «Ventas», «Documentos», «Conversión» y «Ticket» incluyen la tienda. «Activas»
 * no: la tienda no tiene estados logísticos y una venta de tienda o está
 * facturada o está pendiente, no «abierta».
 */
const cards = computed(() => {
  if (summary.value === null) return []

  const { kpis, range } = summary.value

  const delMes = `Del ${formatShortDate(range.from)} al ${formatShortDate(range.to)}`

  return [
    {
      label: 'Ventas facturadas',
      value: formatCurrency(kpis.total_sales),
      context: delMes,
      footnote: `${kpis.invoiced_documents} de ${kpis.documents_in_range} documentos facturados`,
      icon: 'pi pi-euro',
      tone: 'success' as const,
      to: 'quotes.index',
    },
    {
      label: 'Documentos',
      value: String(kpis.documents_in_range),
      context: `${kpis.quotes_in_range} ${kpis.quotes_in_range === 1 ? 'cotización' : 'cotizaciones'} · ${kpis.store_sales_in_range} ${kpis.store_sales_in_range === 1 ? 'venta tienda' : 'ventas tienda'}`,
      footnote: `${kpis.invoiced_documents} facturados en el rango`,
      icon: 'pi pi-clipboard',
      tone: 'info' as const,
      to: 'quotes.index',
    },
    {
      label: 'Tasa de conversión',
      value: `${kpis.conversion_rate} %`,
      context: `${kpis.invoiced_documents} de ${kpis.documents_in_range} documentos facturados`,
      footnote: 'Porcentaje de documentos que acabaron facturados',
      icon: 'pi pi-chart-line',
      tone: 'info' as const,
      to: 'quotes.index',
    },
    {
      label: 'Ticket promedio',
      value: formatCurrency(kpis.average_ticket),
      context: 'Por documento facturado',
      footnote: 'Sirve para saber si el negocio crece en pedidos o en tamaño',
      icon: 'pi pi-wallet',
      tone: 'brand' as const,
      to: undefined,
    },
    {
      label: 'Cotizaciones activas',
      value: String(kpis.active_quotes),
      context: 'Sin facturar ni enviar',
      footnote: 'Solo cotizaciones: la tienda no tiene estados logísticos',
      icon: 'pi pi-file',
      tone: kpis.active_quotes > 0 ? ('warn' as const) : ('neutral' as const),
      to: 'quotes.index',
    },
  ]
})

/**
 * Cómo se reparten los documentos de un cliente.
 *
 * Se escribe el desglose porque decir solo «3 documentos» deja sin saber si son
 * tres cotizaciones, tres ventas de tienda o una mezcla. Los ceros no se pintan:
 * «0 ventas de tienda» es ruido en todas las filas de un cliente que solo cotiza.
 */
function documentosResumen(quotes: number, storeSales: number): string {
  const partes: string[] = []

  if (quotes > 0) partes.push(`${quotes} ${quotes === 1 ? 'cotización' : 'cotizaciones'}`)
  if (storeSales > 0) partes.push(`${storeSales} ${storeSales === 1 ? 'venta de tienda' : 'ventas de tienda'}`)

  return partes.length > 0 ? partes.join(' · ') : 'Sin documentos facturados'
}

/**
 * Una fila de ranking.
 *
 * Los cinco rankings del panel son la misma lista con distinto dato. Lo que les
 * faltaba a todos era lo mismo: la posición, una barra proporcional al primer
 * puesto y las unidades. Con una lista de «nombre — número» no se ve qué vende más
 * que qué, y el panel deja de ordenar nada.
 *
 * La forma vive en `RankList.vue`, que es quien la dibuja: la forma y el dibujo
 * tienen que estar en el mismo sitio. Con la forma aquí y el bloque copiado cuatro
 * veces en el template, las cuatro copias ya habían divergido —una pintaba la
 * posición y otra no, una llevaba el texto auxiliar dentro de la barra y otra
 * aparte— y un ranking nuevo nacería con su propia variante.
 */

/** Escala las filas de un ranking al primer puesto. */
function toRanking(
  rows: Array<{ key: string; primary: string; secondary: string | null; value: string; numeric: number }>,
  tone: string,
): RankRow[] {
  const top = Math.max(...rows.map((row) => row.numeric), 0)

  return rows.map((row) => ({
    key: row.key,
    primary: row.primary,
    secondary: row.secondary,
    value: row.value,
    percent: top > 0 ? Math.round((row.numeric / top) * 100) : 0,
    tone,
  }))
}

const productRank = computed(() =>
  toRanking(
    topProducts.value.map((product) => ({
      key: product.reference,
      primary: product.name,
      secondary: product.reference,
      value: `${product.quantity} uds.`,
      numeric: product.quantity,
    })),
    'bg-primary-500',
  ),
)

const leastProductRank = computed(() =>
  toRanking(
    leastProducts.value.map((product) => ({
      key: product.reference,
      primary: product.name,
      secondary: product.reference,
      value: `${product.quantity} uds.`,
      numeric: product.quantity,
    })),
    'bg-surface-400',
  ),
)

const customerRank = computed(() =>
  toRanking(
    topCustomers.value.map((customer) => ({
      key: customer.customer_name,
      primary: customer.customer_name,
      secondary: documentosResumen(customer.quotes, customer.store_sales),
      value: formatCurrency(customer.total),
      numeric: customer.total,
    })),
    'bg-sky-500',
  ),
)

/**
 * Ticket promedio por cliente.
 *
 * Es el quinto ranking y se calculaba solo para el Excel: el método y la
 * exportación existían, pero el resumen no lo traía, así que en el panel no había
 * nada que pintar. Un ranking que solo se puede descargar no es un ranking del
 * panel, es un fichero.
 */
const averageCustomerRank = computed(() =>
  toRanking(
    topCustomersAvg.value.map((customer) => ({
      key: customer.customer_name,
      primary: customer.customer_name,
      // El número de documentos es el contexto: un ticket alto con una sola
      // compra puede ser casualidad, y con diez es una costumbre.
      secondary: documentosResumen(customer.quotes, customer.store_sales),
      value: formatCurrency(customer.average_ticket),
      numeric: customer.average_ticket,
    })),
    'bg-violet-500',
  ),
)

const advisorRank = computed(() =>
  toRanking(
    topAdvisors.value.map((advisor) => ({
      key: String(advisor.user_id),
      primary: advisor.name,
      secondary: documentosResumen(advisor.quotes, advisor.store_sales),
      value: formatCurrency(advisor.total),
      numeric: advisor.total,
    })),
    'bg-emerald-500',
  ),
)

/**
 * Los tres segmentos, cada uno con su total.
 *
 * Se agrupan en una lista y no se escriben tres bloques a mano porque los tres
 * se leen igual y porque el total es lo que da sentido a la lista: «5
 * recurrentes» no dice nada sin saber cuántos clientes hay en total.
 */
const segmentGroups = computed(() => {
  const segments = summary.value?.segments

  /**
   * El total viene del servidor y no de `data.length`.
   *
   * Los dos son cosas distintas: el total son todos los clientes del segmento y
   * `data` son los que caben en esta página. Contarlos sobre la lista recortada
   * hacía que el panel anunciara «10 recurrentes» cuando había 11, y el número
   * grande —que es lo que se lee de un vistazo— mentía.
   */
  const construir = (
    key: 'new' | 'recurring' | 'inactive',
    label: string,
    page: SegmentPage | undefined,
    hint?: string,
  ) => ({
    key,
    label,
    hint,
    total: page?.total ?? 0,
    page: page?.page ?? 1,
    lastPage: page?.last_page ?? 1,
    items: (page?.data ?? []).map((row) => ({
      name: row.customer_name,
      customerId: row.customer_id,
    })),
  })

  return [
    construir('new', 'Nuevos', segments?.new),
    construir('recurring', 'Recurrentes', segments?.recurring),
    construir(
      'inactive',
      'Inactivos',
      segments?.inactive,
      'Sin cotizaciones vivas ni ventas de tienda en los 30 días previos al cierre del rango. Las cotizaciones canceladas no cuentan como actividad; una venta de tienda, sí.',
    ),
  ]
})

/** Total de cotizaciones facturadas del rango: la referencia de las barras. */
const statusTotal = computed(() =>
  Object.values(summary.value?.status_breakdown ?? {}).reduce((total, count) => total + count, 0),
)

/**
 * Ancho de la barra de un estado, proporcional al total del rango.
 *
 * Sin esto, cuatro números y cuatro etiquetas no dicen nada: hay que compararlos
 * a mano. Con la barra, se ve de un vistazo que un estado concentra la mayoría.
 */
function statusWidth(count: number): string {
  if (statusTotal.value <= 0) return '0%'
  return `${Math.round((count / statusTotal.value) * 100)}%`
}

const STATUS_BAR: Record<string, string> = {
  enviada: 'bg-emerald-500',
  pendiente: 'bg-amber-500',
  separada: 'bg-sky-500',
  empacada: 'bg-sky-500',
  borrador: 'bg-surface-400',
  cancelada: 'bg-red-500',
}

function statusBarClass(status: string): string {
  return STATUS_BAR[status] ?? 'bg-surface-400'
}

/** Seguimientos vencidos: lo único que exige una acción hoy. */
const overdueFollowUps = computed(() => summary.value?.kpis.follow_ups_overdue ?? 0)

/** Si el rango tiene documentos. Un rango vacío es un estado, no un error. */
const hasDocuments = computed(() => summary.value !== null && summary.value.kpis.documents_in_range > 0)

const salesDays = computed(() => summary.value?.daily_trend.filter((point) => point.total > 0) ?? [])

/** El dia mas alto de la tendencia, para escalar las barras. */
const peak = computed(() => salesDays.value.reduce((max, point) => Math.max(max, point.total), 0))

function barWidth(total: number): string {
  if (peak.value <= 0) return '0%'
  return `${Math.round((total / peak.value) * 100)}%`
}

const statusEntries = computed(() => {
  const breakdown = summary.value?.status_breakdown ?? {}
  return Object.entries(breakdown)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({ status, label: statusLabel(status), count }))
})

const topProducts = computed<ProductRank[]>(() => summary.value?.top_products ?? [])
const leastProducts = computed<ProductRank[]>(() => summary.value?.least_products ?? [])
const topCustomers = computed(() => summary.value?.top_customers ?? [])
const topCustomersAvg = computed(() => summary.value?.top_customers_avg ?? [])
const topAdvisors = computed(() => summary.value?.top_advisors ?? [])
const followUps = computed(() => summary.value?.pending_follow_ups ?? [])

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar el panel. Revisa tu conexion e intentalo de nuevo.'
}

/**
 * Página de cada segmento.
 *
 * Vive aquí y no en la URL a propósito, a diferencia de los filtros del listado
 * de cotizaciones. Cambiar de página aquí recorta una lista del panel, y dejar
 * eso en el histórico hace que el botón de atrás del navegador devuelva al panel
 * un estado que ya no tiene sentido —una lista acortada que nadie pidió volver a
 * ver—. Los filtros, que sí describen qué se está mirando, van en la URL.
 */
const segmentPages = ref<Record<'new' | 'recurring' | 'inactive', number>>({
  new: 1,
  recurring: 1,
  inactive: 1,
})

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await dashboardApi.summary({
      date_from: dateFrom.value === '' ? undefined : dateFrom.value,
      date_to: dateTo.value === '' ? undefined : dateTo.value,
      page_new_customers: segmentPages.value.new,
      page_recurring_customers: segmentPages.value.recurring,
      page_inactive_customers: segmentPages.value.inactive,
    })
    summary.value = data.data
  } catch (cause: unknown) {
    summary.value = null
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

/** Cambia la página de un segmento y recarga. */
async function irASegmento(key: string, page: number): Promise<void> {
  if (key !== 'new' && key !== 'recurring' && key !== 'inactive') return

  segmentPages.value = { ...segmentPages.value, [key]: Math.max(1, page) }
  await load()
}

watch([dateFrom, dateTo], () => {
  // Cambiar el rango deja sin sentido la página en la que se estaba: los
  // segmentos son de otro conjunto y quedarse en la tercera puede dejar la lista
  // vacía sin explicación.
  segmentPages.value = { new: 1, recurring: 1, inactive: 1 }
  void load()
})

/**
 * Reinicia el rango al mes en curso.
 *
 * Se calcula en el cliente para que el boton responda al instante, y se manda
 * explicito en la peticion. El backend lo vuelve a confirmar en `data.range`,
 * que es la fecha buena: si el cliente y el servidor calcularan el mes por su
 * cuenta, una descarga hecha a medianoche podria traer un mes distinto al que se
 * esta viendo.
 */
function resetToCurrentMonth(): void {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  // Se formatea a mano en vez de usar `toISOString()`: este convierte a UTC y en
  // una zona horaria negativa devuelve el dia anterior al que se ha pedido.
  const iso = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
  }

  dateFrom.value = iso(first)
  dateTo.value = iso(last)
}

/**
 * Enlace a la ficha de un cliente de un segmento.
 *
 * El nombre del segmento es texto muerto si no lleva a ningún sitio: el comercial
 * lee el nombre en la pantalla, lo escribe en el buscador del listado de clientes,
 * abre la ficha y busca sus cotizaciones a mano. Es el bucle más corto del CRM y
 * estaba cortado justo donde debería continuar.
 *
 * Se arrastra el rango del panel para que la ficha abra con las cotizaciones del
 * periodo que el usuario estaba mirando, y `from=dashboard` para que su botón de
 * vuelta sepa a dónde regresar. El rango viaja en la URL y no en un almacén porque
 * es estado de navegación: el botón de atrás del navegador tiene que devolver la
 * vista a como estaba.
 *
 * Se devuelve `null` cuando el segmento viene sin `customer_id`. El panel agrupa
 * por nombre, que es una copia de la cotización, y una cotización puede no tener
 * cliente: sin identificador no hay ficha a la que ir, y un enlace a la nada es
 * peor que no ofrecerlo.
 */
function segmentCustomerTo(customerId: number | null): { name: string; params: Record<string, string>; query: Record<string, string> } | null {
  if (customerId === null) return null

  const query: Record<string, string> = { from: 'dashboard' }

  if (dateFrom.value !== '') query.date_from = dateFrom.value
  if (dateTo.value !== '') query.date_to = dateTo.value

  return { name: 'customers.show', params: { id: String(customerId) }, query }
}

/**
 * Descarga una exportacion.
 *
 * Pasa por `dashboardApi.download()` y no por un `<a href>` al que solo se le pone
 * la URL. Un enlace del documento no lleva la cabecera `Authorization`: llega sin
 * token, el backend responde 401 y el navegador se queda con un JSON de error en
 * lugar del `.xlsx`. Por axios el token lo pone el interceptor, y si ha caducado se
 * refresca solo y se reintenta sin que el usuario haga nada.
 */
const downloading = ref<string | null>(null)

async function download(path: string): Promise<void> {
  if (downloading.value !== null) return

  downloading.value = path
  errorMessage.value = null

  try {
    await dashboardApi.download(path, {
      date_from: dateFrom.value === '' ? undefined : dateFrom.value,
      date_to: dateTo.value === '' ? undefined : dateTo.value,
    })
  } catch (cause: unknown) {
    const maybe = cause as { message?: string }

    errorMessage.value = maybe.message ?? 'No se pudo descargar el fichero.'
  } finally {
    downloading.value = null
  }
}

onMounted(async () => {
  if (!allowed.value) return

  resetToCurrentMonth()
  await load()
})
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Panel de ventas
        </h1>
        <p
          v-if="summary"
          class="mt-1 text-sm text-surface-500 dark:text-surface-400"
          data-testid="dashboard-range"
        >
          Del {{ formatShortDate(summary.range.from) }} al {{ formatShortDate(summary.range.to) }}
        </p>
        <p
          v-else
          class="mt-1 text-sm text-surface-500 dark:text-surface-400"
        >
          Cifras del periodo seleccionado
        </p>
      </div>
    </header>

    <Message
      v-if="!allowed"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      El panel de ventas es de acceso restringido: tu rol no tiene el permiso
      <code>dashboard.access</code>.
    </Message>

    <template v-else>
      <!--
        Filtro de rango.

        Va en una rejilla de dos columnas iguales y una automática en vez de tres
        elementos en un `flex`. Con `flex` cada campo se dimensionaba al ancho de
        su contenido, así que los dos selectores de fecha acababan con anchos
        distintos —un input de fecha nativo mide según su contenido— y no había
        forma de alinearlos. Con la rejilla las dos columnas miden igual por
        construcción.
      -->
      <AppCard>
        <form
          class="grid grid-cols-1 items-end gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto]"
          role="search"
          @submit.prevent="load"
        >
          <div class="min-w-0">
            <DateField
              v-model="dateFrom"
              input-id="dashboard-from"
              label="Desde"
            />
          </div>

          <div class="min-w-0">
            <DateField
              v-model="dateTo"
              input-id="dashboard-to"
              label="Hasta"
              :min="dateFrom"
            />
          </div>

          <Button
            label="Mes en curso"
            icon="pi pi-calendar"
            severity="secondary"
            type="button"
            class="w-full sm:w-auto"
            :loading="loading"
            @click="resetToCurrentMonth"
          />
        </form>
      </AppCard>

      <!-- Estado de error -->
      <div
        v-if="errorMessage"
        class="flex flex-col items-center gap-3 py-12 text-center"
        role="alert"
      >
        <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          No se pudo cargar el panel
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{ errorMessage }}
        </p>
        <Button
          label="Reintentar"
          icon="pi pi-refresh"
          severity="secondary"
          @click="load"
        />
      </div>

      <AppCard v-else-if="loading">
        <div class="space-y-4">
          <Skeleton
            v-for="index in 4"
            :key="index"
            height="3rem"
          />
        </div>
      </AppCard>

      <template v-else-if="summary">
        <!-- Rango sin documentos: un estado propio, no ceros sin contexto -->
        <AppCard v-if="!hasDocuments">
          <div class="flex flex-col items-center gap-3 py-10 text-center">
            <i class="pi pi-calendar-times text-4xl text-surface-300 dark:text-surface-600" />
            <p class="font-medium text-surface-900 dark:text-surface-100">
              No hay documentos en este rango
            </p>
            <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
              Entre el {{ formatShortDate(summary.range.from) }} y el {{ formatShortDate(summary.range.to) }} no se ha
              registrado ninguna cotización ni venta de tienda. Amplía el rango o vuelve al mes en curso.
            </p>
            <Button
              label="Volver al mes en curso"
              icon="pi pi-calendar"
              severity="secondary"
              outlined
              @click="resetToCurrentMonth"
            />
          </div>
        </AppCard>

        <template v-else>
          <!--
            Resumen del periodo.

            La etiqueta de sección no es adorno: sin ella, cinco rejillas de
            números seguidas no dicen si son del mes, del año o de la empresa, y
            el lector tiene que subir a la cabecera a buscar el rango.
          -->
          <section aria-labelledby="panel-resumen">
            <h2
              id="panel-resumen"
              class="mb-3 text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400"
            >
              Resumen del periodo
            </h2>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <StatCard
                v-for="card in cards"
                :key="card.label"
                :label="card.label"
                :value="card.value"
                :context="card.context"
                :footnote="card.footnote"
                :icon="card.icon"
                :tone="card.tone"
                :to="card.to"
              />
            </div>
          </section>

          <!--
            Cómo evoluciona y cómo se reparte. Se agrupan porque las dos
            preguntas son la misma mirada: de dónde sale el total del periodo.
          -->
          <section aria-labelledby="panel-evolucion">
            <h2
              id="panel-evolucion"
              class="mb-3 text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400"
            >
              Cómo evoluciona y cómo se reparte
            </h2>

            <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <AppCard class="lg:col-span-2">
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Ventas por día
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Solo los días con ventas. La barra es proporcional al mejor día
                      del rango.
                    </p>
                  </div>
                </template>

                <ol
                  v-if="salesDays.length > 0"
                  class="space-y-3"
                  data-testid="dashboard-trend"
                >
                  <li
                    v-for="point in salesDays"
                    :key="point.date"
                  >
                    <div class="flex items-baseline justify-between gap-3">
                      <span class="text-sm tabular-nums text-surface-900 dark:text-surface-100">
                        {{ formatShortDate(point.date) }}
                      </span>
                      <span class="shrink-0 text-sm font-semibold tabular-nums text-surface-900 dark:text-surface-100">
                        {{ formatCurrency(point.total) }}
                      </span>
                    </div>

                    <div class="mt-1.5">
                      <span class="h-1.5 block overflow-hidden rounded-full bg-surface-100 dark:bg-surface-800">
                        <span
                          class="block h-full rounded-full bg-primary-500"
                          :style="{ width: barWidth(point.total) }"
                        />
                      </span>
                    </div>
                  </li>
                </ol>

                <p
                  v-else
                  class="py-8 text-center text-sm text-surface-500 dark:text-surface-400"
                >
                  Ningún día del rango tiene ventas.
                </p>
              </AppCard>

              <AppCard>
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Reparto por estado
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Cotizaciones facturadas del rango por estado. La barra es el
                      porcentaje sobre el total, que es la referencia que hace
                      comparables las barras entre sí.
                    </p>
                  </div>
                </template>

                <ol
                  v-if="statusEntries.length > 0"
                  class="space-y-3"
                  data-testid="dashboard-status"
                >
                  <li
                    v-for="entry in statusEntries"
                    :key="entry.status"
                  >
                    <div class="flex items-baseline justify-between gap-3">
                      <Tag
                        :value="entry.label"
                        :severity="statusSeverity(entry.status)"
                      />
                      <span class="shrink-0 text-sm font-semibold tabular-nums text-surface-900 dark:text-surface-100">
                        {{ entry.count }}
                      </span>
                    </div>

                    <div class="mt-1.5">
                      <span class="h-1.5 block overflow-hidden rounded-full bg-surface-100 dark:bg-surface-800">
                        <span
                          :class="['block h-full rounded-full', statusBarClass(entry.status)]"
                          :style="{ width: statusWidth(entry.count) }"
                        />
                      </span>
                    </div>
                  </li>
                </ol>

                <p
                  v-else
                  class="py-8 text-center text-sm text-surface-500 dark:text-surface-400"
                >
                  No hay cotizaciones facturadas en el rango.
                </p>

                <!--
                  El total va al pie porque sin él los porcentajes no se pueden
                  comprobar: con cuatro barras proporcionales y ningún total, «60 %» no
                  dice si son 6 cotizaciones o 600.
                -->
                <p
                  v-if="statusEntries.length > 0"
                  class="mt-3 border-t border-surface-100 pt-2 text-sm text-surface-500 dark:border-surface-700 dark:text-surface-400"
                >
                  Total facturado en el rango:
                  <strong class="tabular-nums">{{ statusTotal }}</strong>
                </p>
              </AppCard>
            </div>
          </section>

          <!--
            Rankings del periodo. Los cinco son el mismo dibujo con distinto dato,
            y por eso comparten `RankList`: el bloque estaba copiado cuatro veces y
            las copias ya divergían entre sí.
          -->
          <section aria-labelledby="panel-rankings">
            <h2
              id="panel-rankings"
              class="mb-3 text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400"
            >
              Rankings del periodo
            </h2>

            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <AppCard>
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Productos más vendidos
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Unidades facturadas en cotizaciones. La barra es el porcentaje
                      respecto al primero. Las no facturadas no cuentan aquí, los
                      artículos de servicio quedan fuera y las ventas de tienda no
                      tienen artículos que sumar.
                    </p>
                  </div>
                </template>

                <RankList
                  :rows="productRank"
                  tone="bg-primary-500"
                  :secondary="80"
                  empty="No se facturó ningún producto del catálogo en el rango."
                  testid="rank-top-products"
                />
              </AppCard>

              <AppCard>
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Clientes por total facturado
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Suma de lo facturado en el rango: cotizaciones y ventas de
                      tienda, con el desglose de cada cliente.
                    </p>
                  </div>
                </template>

                <RankList
                  :rows="customerRank"
                  tone="bg-sky-500"
                  :secondary="112"
                  empty="No se facturó nada a ningún cliente del rango."
                  testid="rank-top-customers"
                />
              </AppCard>

              <!--
                El ticket promedio va pegado al gasto total y no en otro sitio del
                panel porque son el mismo grupo de gente ordenado por dos criterios
                distintos: uno dice a quién hay que atender por volumen, el otro a
                quién por costumbre de compra. Juntos se leen como dos respuestas a
                «a quién llamo primero».
              -->
              <AppCard>
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Clientes por ticket promedio
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Promedio de lo facturado por documento de cada cliente, sea
                      cotización o venta de tienda. No es el mismo ranking que el de
                      gasto total: dos compras de $ 5.000 dan el mismo ticket que
                      una de $ 10.000.
                    </p>
                  </div>
                </template>

                <RankList
                  :rows="averageCustomerRank"
                  tone="bg-violet-500"
                  :secondary="112"
                  empty="No hay tickets promedio calculables en el rango."
                  testid="rank-top-customers-avg"
                />
              </AppCard>

              <AppCard>
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Asesores por ventas
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Solo los que tienen documentos facturados asignados:
                      cotizaciones y ventas de tienda.
                    </p>
                  </div>
                </template>

                <RankList
                  :rows="advisorRank"
                  tone="bg-amber-500"
                  empty="Ninguna venta del rango tiene asesor asignado."
                  testid="rank-top-advisors"
                />
              </AppCard>

              <AppCard>
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Productos menos vendidos
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Finalizan la lista porque hay que mirar el catálogo: lo que no
                      se vende ocupa espacio y capital. Solo cotizaciones: la tienda
                      no tiene artículos.
                    </p>
                  </div>
                </template>

                <RankList
                  :rows="leastProductRank"
                  tone="bg-surface-400"
                  :secondary="80"
                  empty="No hay datos de productos en el rango."
                  testid="rank-least-products"
                />
              </AppCard>
            </div>
          </section>

          <!--
            Segmentos y trabajo pendiente.

            Los segmentos son tres grupos que se excluyen entre sí, así que van
            con su número: sin el total, no hay forma de saber si «5 recurrentes»
            es mucho o poco. Y los seguimientos vencidos van aparte porque son lo
            único de todo el panel que exige una acción hoy.
          -->
          <section aria-labelledby="panel-clientes">
            <h2
              id="panel-clientes"
              class="mb-3 text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400"
            >
              Clientes y trabajo pendiente
            </h2>

            <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <AppCard class="lg:col-span-2">
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Segmentos de clientes
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Los tres grupos son excluyentes: un cliente no está en dos a
                      la vez, así que la suma es el total de clientes con actividad.
                    </p>
                  </div>
                </template>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div
                    v-for="group in segmentGroups"
                    :key="group.key"
                    class="rounded-lg border border-surface-200 p-4 dark:border-surface-700"
                    :data-testid="`segment-${group.key}`"
                  >
                    <p class="text-xs font-medium uppercase tracking-wide text-surface-500 dark:text-surface-400">
                      {{ group.label }}
                    </p>
                    <p class="mt-1 text-2xl font-semibold tabular-nums text-surface-900 dark:text-surface-100">
                      {{ group.total }}
                    </p>

                    <!--
                      La regla de «inactivo» se escribe al lado del número.
                      Treinta días sin actividad no es evidente, y un comercial
                      que ve 400 inactivos necesita saber qué los separa de los
                      demás para decidir a quién llamar.
                    -->
                    <p
                      v-if="group.hint"
                      class="mt-1 text-xs leading-snug text-surface-500 dark:text-surface-400"
                    >
                      {{ group.hint }}
                    </p>

                    <ul
                      v-if="group.items.length > 0"
                      class="mt-3 space-y-0.5 border-t border-surface-100 pt-3 dark:border-surface-800"
                    >
                      <li
                        v-for="item in group.items"
                        :key="`${item.customerId}-${item.name}`"
                      >
                        <!--
                          `router-link` y no un `div` con un manejador de clic: un
                          `div` solo responde pulsando dentro, no se puede tabular y no
                          se anuncia. Y el enlace se ve como enlace —subrayado al
                          pasar por encima y foco visible—, porque si el nombre parece
                          texto corriente el clic no se intenta ni se sabe que hay
                          algo debajo.

                          El total del segmento y su nombre se quedan sin pulsar a
                          propósito: son cinco clientes y no hay ficha a la que ir. Un
                          clic que promete algo que no puede cumplir es peor que no
                          ofrecerlo.

                          Solo el nombre. El panel dice **a quién** mirar; el detalle
                          de cada cuenta —asesores, importe, cotizaciones, fecha— está
                          en el Excel del segmento. Con eso aquí, cada fila eran tres
                          columnas de texto y el bloque no se leía de un vistazo. Y un
                          panel no es donde se trabaja una cartera: es donde se decide
                          a quién mirar.
                        -->
                        <RouterLink
                          v-if="segmentCustomerTo(item.customerId) !== null"
                          :to="segmentCustomerTo(item.customerId)!"
                          class="block truncate rounded py-1 text-sm underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                          :aria-label="`Ver la ficha de ${item.name}`"
                        >
                          <span class="text-primary-700 dark:text-primary-300">
                            {{ item.name }}
                          </span>
                        </RouterLink>

                        <!--
                          Sin `customer_id` no hay ficha a la que ir. Se muestra el
                          nombre como texto, no como enlace: es el caso de una
                          cotización sin cliente enlazado, y un enlace roto ahí sería
                          un fallo del panel.
                        -->
                        <span
                          v-else
                          class="block truncate py-1 text-sm text-surface-600 dark:text-surface-300"
                        >
                          {{ item.name }}
                        </span>
                      </li>
                    </ul>

                    <p
                      v-else
                      class="mt-3 border-t border-surface-100 pt-3 text-sm text-surface-400 dark:border-surface-800"
                    >
                      Ninguno en el rango.
                    </p>

                    <!--
                      Paginador por segmento, y no compartido: cada grupo se recorre
                      por separado, y hay que poder estar en la tercera página de
                      inactivos y en la primera de nuevos a la vez. Con uno común,
                      avanzar una lista las mueve todas.

                      Sale solo cuando hay más de una página: con una sola no hay
                      nada que cambiar, y un paginador deshabilitado solo hace ruido.
                    -->
                    <SimplePaginator
                      v-if="group.lastPage > 1"
                      :page="group.page"
                      :last-page="group.lastPage"
                      :label="group.label"
                      :testid="`segment-paginator-${group.key}`"
                      @change="(pagina) => irASegmento(group.key, pagina)"
                    />
                  </div>
                </div>
              </AppCard>

              <AppCard>
                <template #header>
                  <div>
                    <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                      Seguimientos pendientes
                    </h3>
                    <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                      Vencidos y los de hoy. Son los que piden una llamada.
                    </p>
                  </div>
                </template>

                <div
                  v-if="overdueFollowUps > 0"
                  class="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/60 dark:bg-amber-950/30"
                  data-testid="dashboard-overdue"
                >
                  <i
                    class="pi pi-exclamation-triangle text-xl text-amber-500"
                    aria-hidden="true"
                  />
                  <div>
                    <p class="text-2xl font-semibold leading-none tabular-nums text-amber-700 dark:text-amber-300">
                      {{ overdueFollowUps }}
                    </p>
                    <p class="mt-1 text-sm text-amber-800 dark:text-amber-200">
                      {{ overdueFollowUps === 1 ? 'seguimiento vencido' : 'seguimientos vencidos' }}
                    </p>
                  </div>
                </div>

                <p
                  v-if="followUps.length === 0"
                  class="py-8 text-center text-sm text-surface-500"
                >
                  No hay seguimientos vencidos ni para hoy.
                </p>

                <ul
                  v-else
                  class="divide-y divide-surface-100 dark:divide-surface-800"
                >
                  <li
                    v-for="followUp in followUps"
                    :key="followUp.id"
                    class="flex items-center gap-3 py-2.5 first:pt-0"
                  >
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-medium text-surface-900 dark:text-surface-100">
                        {{ followUp.customer_name }}
                      </p>
                      <p class="text-xs text-surface-500 dark:text-surface-400">
                        {{ followUp.next_follow_up_date ? formatShortDate(followUp.next_follow_up_date) : 'Sin fecha' }}
                        · {{ followUp.status }}
                      </p>
                    </div>

                    <span
                      v-if="followUp.unread_notes_count > 0"
                      class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                    >
                      {{ followUp.unread_notes_count }} nueva(s)
                    </span>
                  </li>
                </ul>
              </AppCard>
            </div>
          </section>
        </template>

        <!--
          Exportaciones.

          Fuera del bloque de datos a propósito: son una acción, no parte del
          panel. Van al final para que se lean como el último paso y no como una
          tabla más.
        -->
        <AppCard>
          <template #header>
            <div>
              <h2 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                Exportar a Excel
              </h2>
              <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                Las nueve descargas respetan el rango seleccionado, así que el
                fichero contiene exactamente lo que se está viendo.
              </p>
            </div>
          </template>

          <div class="flex flex-wrap gap-2">
            <!--
              Cada botón muestra su propio estado de carga. Sin él, la descarga
              tarda lo que tarda el servidor y no se ve que esté pasando nada: un
              botón que no cambia durante un segundo largo se lee como que no ha
              pulsado, y la respuesta habitual es volver a pulsarlo.

              Mientras uno descarga, los demás se apagan: son la misma consulta con
              otro recorte, y pulsar cinco veces seguidas genera cinco ficheros
              que el usuario no ha pedido.
            -->
            <Button
              v-for="item in DASHBOARD_EXPORTS"
              :key="item.key"
              :label="item.label"
              icon="pi pi-download"
              size="small"
              severity="secondary"
              outlined
              :loading="downloading === item.path"
              :disabled="downloading !== null && downloading !== item.path"
              @click="download(item.path)"
            />
          </div>

          <!--
            El error de descarga va junto a los botones, no en el panel de error de
            arriba: es de esta sección y no del resumen. Si apareciera arriba, con
            el panel entero intacto, se leería como que los datos fallaron.
          -->
          <Message
            v-if="errorMessage !== null"
            severity="error"
            :closable="true"
            class="mt-3"
            @close="errorMessage = null"
          >
            {{ errorMessage }}
          </Message>
        </AppCard>
      </template>
    </template>
  </div>
</template>
